"use client";

import React, { useEffect, useRef } from "react";

export interface BroadbandFactsWebComponentProps {
  offerId: string;
  sessionId?: string;
  geoCode?: string;
  state?: string;
  servicePlanType?: string;
  lineOfBusiness?: string;
  headerTag?: string;
  labelId?: string;
  mobileSegmentLevel?: string;
  onResize?: () => void;
  sourceServerId?: string;
  sourceSystemId?: string;
  className?: string;
  testId?: string;
}

let scriptInjected = false;
let isInjecting = false;

const BroadbandFactsWebComponent: React.FC<BroadbandFactsWebComponentProps> = ({
  offerId,
  sessionId,
  geoCode,
  state,
  servicePlanType = "POSTPAID",
  lineOfBusiness = "INTERNET",
  headerTag,
  labelId,
  mobileSegmentLevel,
  onResize,
  sourceServerId = "Dotcom",
  sourceSystemId = "Xfinity-Learn",
  className = "",
  testId = "broadband-facts-web-component",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (scriptLoadedRef.current || scriptInjected || isInjecting) {
      return;
    }

    const existingScript = document.querySelector(
      'script[id="xfinity-broadband-facts"], script[src*="broadband-label-ui.esm.js"]'
    );

    if (existingScript) {
      scriptInjected = true;
      scriptLoadedRef.current = true;
      return;
    }

    isInjecting = true;

    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "https://xfinity.com";

    // Load script file (CSS is bundled within the component)
    const script = document.createElement("script");
    script.id = "xfinity-broadband-facts";
    script.src = `${baseUrl}/digital/broadband/broadband-label-ui.esm.js`;
    script.type = "module";
    script.async = true;

    script.onload = () => {
      isInjecting = false;
      scriptInjected = true;
      scriptLoadedRef.current = true;
    };

    script.onerror = () => {
      isInjecting = false;
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!containerRef.current || !onResize) {
      return;
    }

    const container = containerRef.current;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (entries.length > 0) {
          onResize();
        }
      });
    });

    resizeObserverRef.current.observe(container);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [onResize]);

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={testId}
      style={{ minHeight: "210px" }}
    >
      <xfinity-broadband-facts
        tier-ids={offerId}
        service-plan-type={servicePlanType}
        line-of-business={lineOfBusiness}
        session-id={sessionId}
        geo-code={geoCode}
        state={state}
        header-tag={headerTag}
        label-id={labelId}
        mobile-segment-level={mobileSegmentLevel}
        source-server-id={sourceServerId}
        source-system-id={sourceSystemId}
      />
    </div>
  );
};

export default BroadbandFactsWebComponent;
